import { CountdownTimer } from '@web/graphql-components'
import { ComponentType, PageComponent, DonationPageLayoutFragment, Column } from '@web/graphql-queries/graphql'
import { ReactElement } from 'react'
import { checkIsHiddenFromDates } from '../utils/dateUtils'

export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export function findComponentByFunction<T>(
  layout: DonationPageLayoutFragment['desktop'],
  components: Map<string, PageComponent>,
  fn: (c: PageComponent) => boolean,
): T | null {
  const search = [...layout]

  while (search.length > 0) {
    const current = search.shift()

    if (!current) continue

    const component = components.get(current.id)
    if (component && fn(component)) {
      return component as T
    }

    if (current.childComponents && current.childComponents.length > 0) {
      // Add child components to the front of the queue for deep search
      search.unshift(...current.childComponents)
    }
  }

  return null
}

// grabbing the first background image or image
export const findImage = (c: PageComponent) => {
  if (c.type === ComponentType.Row || c.type === ComponentType.Column) {
    return Boolean(c.styles?.backgroundImage)
  }

  return c.type === ComponentType.Image
}

export type PageComponentProps = {
  id: string
  allComponents?: Map<string, PageComponent>
  childComponents?: PageComponentProps[] | null
  component?: PageComponent
  isWidthSet?: boolean
}

export type ComponentWrapper = { Component: (props: PageComponentProps) => ReactElement | null }

export type SubComponentProps = PageComponentProps & {
  parentId: string
  name: string
  overrides?: Partial<PageComponent>
}

const RANDOM_COLORS = ['red', 'blue', 'green', 'yellow', 'purple', 'orange']
export const pickPalette = (name: string) => {
  const index = name.charCodeAt(0) % RANDOM_COLORS.length
  return RANDOM_COLORS[index]
}

export const MOBILE_CONTAINER_QUERY = '@container page (min-width: 0px)'
export const DESKTOP_CONTAINER_QUERY = '@container page (min-width: 901px)'

export enum MenuItemLinkStyle {
  Primary = 'primary',
  Secondary = 'secondary',
  Standard = 'standard',
}

export type MenuItemLink = {
  id: string
  index: number
  label: string
  url: string
  style: MenuItemLinkStyle
}

export const DONATION_FORM_ELEMENT_ID = '#donation-form-element'

const LAYOUT_ROOT_IDS = ['mobile-layout', 'desktop-layout']

/** Scroll to an anchor inside the layout that is actually shown. Both layouts stay in the DOM. */
export const scrollToVisibleAnchor = (anchor: string) => {
  if (typeof document === 'undefined') {
    return
  }

  const id = anchor.replace(/^#/, '')
  if (!id) {
    return
  }

  const layout = LAYOUT_ROOT_IDS.map((layoutId) => document.getElementById(layoutId)).find(
    (element) => element && window.getComputedStyle(element).display !== 'none',
  )
  const target = layout?.querySelector(`#${CSS.escape(id)}`)

  if (target instanceof HTMLElement) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

export const checkIfComponentVisible = (component: PageComponentProps, map: Map<string, PageComponent>): boolean => {
  const c = map.get(component.id)

  if (!c) {
    return false
  }

  switch (c.type) {
    case ComponentType.CountdownTimer: {
      if (typeof window === 'undefined') {
        return true
      }

      const { start, end, finish } = c as CountdownTimer
      const isHidden = checkIsHiddenFromDates(start, end ? finish : '', null)

      return !isHidden
    }
    case ComponentType.Column: {
      const column = c as Column

      if (!column.styles?.hideWhenChildrenHidden) {
        return true
      }

      return (
        component.childComponents?.every((childComponent) => {
          return checkIfComponentVisible(childComponent, map)
        }) ?? true
      )
    }
    default: {
      return true
    }
  }
}
